import i18n from 'src/lib/i18n/i18n';
import View from '@play-co/timestep-core/lib/ui/View';
import { Feature } from 'src/replicant/analytics';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import buttons from 'src/lib/ui/config/buttons';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import sounds from 'src/lib/sounds';
import { createUIPet } from 'src/game/pet/pet.utils';
import { StateSummary } from 'src/replicant/guest/guest.getters';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import {
  evolutionSequence,
  getEvolutionStage,
} from 'src/replicant/pet/pet.ruleset';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (selected: StateSummary | undefined) => void;

export default class PopupSelectSave extends PopupBasicLazy<CloseHandler> {
  private readonly savesContainer: FlexBoxView;
  private selectedSave: StateSummary | undefined = undefined;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      width: downscale(852),
      height: downscale(1164),
      zIndex: 10000,
      ...opts,
      tracking: {
        feature: Feature.NATIVE._,
        $subFeature: Feature.NATIVE.GUEST_SELECT_SAVE,
      },
    });
    this.buttonClose.removeFromSuperview();

    const flexContainer = new FlexBoxView({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
      flex: {
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'column',
        paddingTop: 80,
        paddingBottom: 20,
      },
    });

    const textContainer = new FlexBoxView({
      superview: flexContainer,
      width: this.box.style.width,
      height: 90,
      flex: {
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'column',
      },
    });

    new LangBitmapFontTextView({
      superview: textContainer,
      width: this.box.style.width - 50,
      height: 70,
      localeText: () => i18n(`chooseSave.description`),
      size: 24,
      wordWrap: true,
      color: '#BF8147',
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
    });

    new LangBitmapFontTextView({
      superview: textContainer,
      width: this.box.style.width,
      height: 24,
      localeText: () => i18n(`chooseSave.warning`),
      size: 24,
      color: '#EA2727',
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
    });

    this.savesContainer = new FlexBoxView({
      superview: flexContainer,
      width: this.box.style.width,
      height: 400,
      flex: {
        justifyContent: 'space-evenly',
        alignItems: 'center',
      },
    });

    new ButtonScaleViewWithText({
      superview: flexContainer,
      ...buttons.secondary,
      width: downscale(335),
      height: downscale(100),
      fontSize: 25,
      font: 'Body',
      tracking: {
        feature: Feature.NATIVE._,
        $subFeature: Feature.NATIVE.GUEST_SELECT_SAVE,
      },
      localeText: () => i18n(`chooseSave.confirm`),
      onClick: async () => {
        sounds.sfx.play('ok');
        opts.close(this.selectedSave);
      },
    });
  }

  public init(opts: { states: StateSummary[] }) {
    super.init({
      title: i18n(`chooseSave.title`),
    });

    // Select the state with most progress.
    // Highest Levels, then Gems, then Points, then Evolution Stage, then Last Played
    this.selectedSave = opts.states.slice().sort((a, b) => {
      if (a.matchLevel !== b.matchLevel) {
        return b.matchLevel - a.matchLevel;
      }
      if (a.gems !== b.gems) {
        return b.gems - a.gems;
      }
      if (a.points !== b.points) {
        return b.points - a.points;
      }

      const evolutionA = evolutionSequence.indexOf(
        getEvolutionStage('control', a.species ?? 'ball'),
      );
      const evolutionB = evolutionSequence.indexOf(
        getEvolutionStage('control', b.species ?? 'ball'),
      );
      if (evolutionA !== evolutionB) {
        return evolutionB - evolutionA;
      }

      return b.lastPlayedAt - a.lastPlayedAt;
    })[0];

    const stateViews = opts.states.map((state) => this.createSaveItem(state));

    // Update the selected state based on the initial selection.
    const updateSelection = (selected: StateSummary) =>
      stateViews.forEach(({ state, setSelected }) =>
        setSelected(selected.origin === state.origin),
      );

    // Add selections listeners to each state view
    stateViews.forEach(({ view, state }) => {
      view.on('InputSelect', () => {
        this.selectedSave = state;
        updateSelection(this.selectedSave);
      });
    });
  }

  /**
   * Preload assets before showing the popup
   */
  protected getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/ui/guest/']),
      ...AssetGroup.constructURLs(['assets/ui/shared/']),
      ...AssetGroup.constructURLs(['assets/ui/hud/icons/']),
    ]);
  }

  private createSaveItem(state: StateSummary) {
    const saveItem = new FlexBoxView({
      superview: this.savesContainer,
      width: downscale(368),
      height: downscale(670),
      flex: {
        justifyContent: 'space-around',
        alignItems: 'center',
        flexDirection: 'column',
      },
    });

    const bg = new ImageScaleView({
      superview: saveItem,
      width: downscale(368),
      height: downscale(611),
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 30, center: 10, right: 30 },
        vertical: { top: 30, middle: 10, bottom: 30 },
      },
      imgScale: 1.7,
    });

    new LangBitmapFontTextView({
      superview: saveItem,
      width: downscale(368),
      height: downscale(40),
      localeText: () =>
        i18n(`chooseSave.${state.origin}`, {
          date: new Date(state.lastPlayedAt).toLocaleDateString(),
        }),
      size: 18,
      color: '#BF8147',
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
    });

    const check = new ImageView({
      superview: bg,
      width: downscale(71),
      height: downscale(71),
      image: `assets/ui/guest/check.png`,
      centerOnOrigin: true,
      x: bg.style.width - downscale(30),
      y: downscale(30),
    });

    const container = new FlexBoxView({
      superview: bg,
      width: bg.style.width,
      height: bg.style.height,
      flex: {
        justifyContent: 'space-evenly',
        alignItems: 'center',
        flexDirection: 'column',
        paddingVertical: 20,
      },
    });

    const pet = createUIPet('happy', state.species);
    pet.updateOpts({
      superview: container,
      width: 160,
      height: 150,
      centerOnOrigin: true,
      scale: 0.6,
    });

    const levelFrame = new ImageScaleView({
      superview: container,
      image: 'assets/ui/guest/frame.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 30, center: 10, right: 30 },
        vertical: { top: 30, middle: 10, bottom: 30 },
      },
      width: downscale(320),
      height: downscale(84),
    });

    new ImageView({
      superview: levelFrame,
      width: downscale(64),
      height: downscale(64),
      image: `assets/ui/guest/level.png`,
      centerOnOrigin: true,
      x: 40,
      y: levelFrame.style.height / 2,
    });

    new LangBitmapFontTextView({
      superview: levelFrame,
      width: downscale(168),
      height: 25,
      localeText: () => i18n(`match2.ui.level`, { level: state.matchLevel }),
      size: 24,
      color: '#9A7171',
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      x: levelFrame.style.width / 2 + 20,
      y: levelFrame.style.height / 2,
    });

    const gemsPanel = new FlexBoxView({
      superview: container,
      width: bg.style.width,
      height: 25,
      flex: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    new ImageScaleView({
      superview: gemsPanel,
      width: downscale(64),
      height: downscale(64),
      image: `assets/ui/hud/icons/hud_gem.png`,
      scaleMethod: 'contain',
    });

    new LangBitmapFontTextView({
      superview: gemsPanel,
      width: 100,
      height: 25,
      localeText: () => state.gems.toLocaleString(),
      size: 24,
      color: '#9A7171',
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
    });

    const coinsPanel = new FlexBoxView({
      superview: container,
      width: bg.style.width,
      height: 25,
      flex: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    new ImageScaleView({
      superview: coinsPanel,
      width: downscale(64),
      height: downscale(64),
      image: `assets/ui/hud/icons/hud_coin.png`,
      scaleMethod: 'contain',
    });
    new LangBitmapFontTextView({
      superview: coinsPanel,
      width: 100,
      height: 25,
      localeText: () => state.points.toLocaleString(),
      size: 24,
      color: '#9A7171',
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
    });

    const setSelected = (selected: boolean) => {
      bg.updateOpts({
        image: selected
          ? `assets/ui/guest/green_frame.png`
          : 'assets/ui/shared/frames/white_frame.png',
      });
      check.updateOpts({
        visible: selected,
      });
    };

    // Make sure the UI reflects the current selected index.
    setSelected(this.selectedSave?.origin === state.origin);

    return {
      state,
      view: bg,
      setSelected,
    };
  }
}
